
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useLog } from "@/use/use-log";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils"
import ShoppingListSearchInputView from "@/components/shoppingList/ShoppingListSearchInputView.vue"
import ShoppingListSearchResultItem from "@/components/shoppingList/ShoppingListSearchResultItem.vue";
import { ShoppingListSearchFoodResultListItem } from "@/types/shoppingList";
import store from "@/store";
import { SelectingIngredientListDataItem } from "@/types/common";
import { 
  ShoppingListSearchFoodResultList,
  ShoppingListSelectSearchResult,
  ShoppingListRegistListItem,
} from "@/types/shoppingList"
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { HTMLDivElementEvent } from "@/types/favorites";
import { isEndOfScrollBottom } from "@/utils/favorites-util";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import router from "@/router";

export default defineComponent({
  name: "ShoppingListSearchResultPage",
  components: {
    ShoppingListSearchInputView,
    CommonDesignDialog,
    ShoppingListSearchResultItem
  },
  setup() {
    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "食材追加"));
    CommonUtil.scroll({ top: 0, left: 0 });

    const { setLogId, outputPageLogs } = useLog();
    NativeClient.sendDebugLog("ShoppingListSearchResultPage setup");

    // 検索対象
    const searchText = ref("");

    // 定数
    const appendButtonText = "追加する";
    const isRaw = true;

    // キーワード
    const initialKeyword = computed(() => {
      if (router.currentRoute.value.query.keyword != undefined && router.currentRoute.value.query.keyword != null) {
        return router.currentRoute.value.query.keyword;
      }
      return "";
    });

    //
    // 画面遷移
    //
    const returnToPreviousPage = () => {
      router.go(-1);
    };
    // const returnToTop = () => {
    //   router.go(-2);
    // };

    // ダイアログ表示タイプ（リトライを行うため）
    class ShoppingListSearchResultPageDialogInfo {
      type: number; // 0:なし、1:検索結果取得、2:食材登録、3:食材登録完了
      nagativeCallback: () => void;
      positiveCallback: () => void;
      constructor(type: number, nagativeCallback: () => void, positiveCallback: () => void) {
        this.type = type;
        this.nagativeCallback = nagativeCallback;
        this.positiveCallback = positiveCallback
      }
    }
    const dummyFunction = () => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage dummyFunction()");
    }
    const refAPIErrorDialogType = ref(new ShoppingListSearchResultPageDialogInfo(0, dummyFunction, dummyFunction));
    const refMessageDialogInfo = ref(new ShoppingListSearchResultPageDialogInfo(0, dummyFunction, dummyFunction));

    // メッセージ・ダイアログ表示関連（ハンドラは種々の関数等をコールするために後述となる）
    const isShowMessageDialog = ref(false);
    const messageDialogType = ref(0);
    const messageDialogTitle = ref("");
    const messageDialogMessage = ref("");
    const messageDialogNegativeLabel = ref("");
    const messageDialogPositiveLabel = ref("");
    const messageDialogNegativeAction = () => {
      isShowMessageDialog.value = false;
      refMessageDialogInfo.value.nagativeCallback();
    };
    const messageDialogPositiveAction = () => {
      isShowMessageDialog.value = false;
      refMessageDialogInfo.value.positiveCallback();
    };
    const showRegistComplete = () => {
      messageDialogType.value = 12;
      messageDialogTitle.value = "食材を追加しました。";
      messageDialogNegativeLabel.value = "閉じる";
      refMessageDialogInfo.value = new ShoppingListSearchResultPageDialogInfo(3, returnToPreviousPage, dummyFunction);
      isShowMessageDialog.value = true;
    };

    // チェック（複数選択する場合も考慮）
    class ShoppingListSerachCheckItem {
      id: number | null;
      name: string;
      constructor(id: number | null, name: string) {
        this.id = id;
        this.name = name;
      }
    }
    const checkedList = ref(Array<ShoppingListSerachCheckItem>());

    //
    // API実行
    //
    const getIngredientCandidate = async (keyword: string) => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage getIngredientCandidate()");
      refAPIErrorDialogType.value = new ShoppingListSearchResultPageDialogInfo(1, dummyFunction, dummyFunction);
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("shoppingList/getSearchResultList", keyword);
      store.dispatch("common/stopLoading");
    };
    const registToShoppingList = async () => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage registToShoppingList() checkedList = " + JSON.stringify(checkedList.value));
      const list: Array<ShoppingListRegistListItem> = [];
      for (const item of checkedList.value) {
        list.push(new ShoppingListRegistListItem(item.id, item.name));
      }
      refAPIErrorDialogType.value = new ShoppingListSearchResultPageDialogInfo(2, dummyFunction, registToShoppingList);
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("shoppingList/registShoppingList", list);
      store.dispatch("common/stopLoading");

      showRegistComplete();
    };

    // 検索文字列をそのまま表示する場合のパラメータ
    const isCompleteSearch = ref(false);
    const rawName = computed(() => {
      return `入力した単語で追加（${searchText.value}）`;
    });
    const rawSelected = computed(() => {
      if (checkedList.value.find(element => element.name == searchText.value && element.id == null)) {
        return true;
      }
      return false;
    });
    const selectItem = (id: number | null, name: string) => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage selectItem id = " + id + ", name = " + name);
      // 将来的に複数選択する場合も考慮しておく
      checkedList.value = [];
      checkedList.value.push(new ShoppingListSerachCheckItem(id, name));
      registToShoppingList();
    };
    const selectRawItem = () => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage selectRawItem");
      checkedList.value = [];
      checkedList.value.push(new ShoppingListSerachCheckItem(null, searchText.value));
      registToShoppingList();
    };

    // 検索文字設定
    let totalCount = 0;
    let ingredientsCount = 0;
    const setSearchText = (target: string) => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage search target keyword = " + target);
      if (target == undefined || target == null || target.length == 0) {
        return;
      }
      searchText.value = target;

      // 新規の検索なので一旦クリアする
      totalCount = 0;
      ingredientsCount = 0;
      store.dispatch("shoppingList/updateSearchResultList", null);

      getIngredientCandidate(target);
    };

    // 表示
    const searchResult = computed(() => {
      const list: ShoppingListSearchFoodResultList  = store.getters["shoppingList/getSearchResultList"];
      const searchResult: Array<SelectingIngredientListDataItem> = [];
      if (list == null) {
        isCompleteSearch.value = false;
      } else {
        NativeClient.sendDebugLog("ShoppingListSearchResultPage searchResult list = " + JSON.stringify(list));
        isCompleteSearch.value = true;
        for (const item of list.foods) {
          const element = new SelectingIngredientListDataItem();
          element.id = item.foodId;
          element.imageUrl = item.image == null ? "" : item.image;
          element.name = item.name;
          NativeClient.sendDebugLog("ShoppingListSearchResultPage searchResult image = " + item.image);
          const findIndex = checkedList.value.findIndex(item => item.id == element.id && item.name == element.name);
          if (findIndex != -1) {
            element.isSelect = true;
          }
          searchResult.push(element);
        }
        NativeClient.sendDebugLog("ShoppingListSearchResultPage searchResult searchResult = " + JSON.stringify(searchResult));
        totalCount = list.totalCount == null ? 0 : list.totalCount;
        ingredientsCount = searchResult.length;
      }
      return searchResult;
    });


    // 食材選択
    // const selectIngredient = (id: number, isSelect: boolean) => {
    //   NativeClient.sendDebugLog("ShoppingListAppendIngredientPage selectIngredient id = " + id + ", isSelect = " + isSelect);
    //   const list: ShoppingListSearchFoodResultList  = store.getters["shoppingList/getSearchResultList"];
    //   if (list == null) {
    //     return;
    //   }
    //   if (isSelect) {
    //     if (!checkedList.value.find(element => element.id == id)) {
    //       // 存在しないので追加する
    //       const findIndex = list.foods.findIndex(element => element.foodId == id);
    //       if (findIndex != -1) {
    //         const element = new ShoppingListSerachCheckItem(id, list.foods[findIndex].name);
    //         checkedList.value.push(element);
    //       }
    //     }
    //   } else {
    //     const findIndex = checkedList.value.findIndex(element => element.id == id);
    //     if (findIndex != -1) {
    //       // 存在するので削除する
    //       checkedList.value.splice(findIndex, 1);
    //     }
    //   }
    //   NativeClient.sendDebugLog("ShoppingListAppendIngredientPage selectIngredient = " + JSON.stringify(checkedList.value));
    // };

    // 「追加する」ボタン
    const styleAppendButton = computed(() => {
      if (checkedList.value.length == 0) {
        // 取得できていない
        return { backgroundColor: `rgba(51,51,51,0.4)`};
      }
      return { backgroundColor: `#333333`};
    });
    const appendButtonAction = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage appendButtonAction");
      if (checkedList.value.length == 0) {
        return;
      }
      registToShoppingList();
    };

    // 表示の制御
    onMounted(() => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage onMounted()");
      if (router.currentRoute.value.query.keyword != undefined && router.currentRoute.value.query.keyword != null) {
        searchText.value = String(router.currentRoute.value.query.keyword);
        getIngredientCandidate(searchText.value);
      }
    });
    onUnmounted(() => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage onUnmounted()");
    });

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("ShoppingListSearchResultPage onScroll totalCount = " + totalCount + ", ingredientsCount = " + ingredientsCount);
      if (
        isEndOfScrollBottom(event) &&
        (ingredientsCount < totalCount) &&
        (searchText.value.length > 0)
      ) {
        NativeClient.sendDebugLog("ShoppingListSearchResultPage onScroll detect");
        getIngredientCandidate(searchText.value);
      }
    };

    // APIリクエスト時のエラーハンドル
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const apiErrorDialogType = 9;
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const cancelHandler = () => {
      closeDialog();
    };
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryHandler = () => {
      closeDialog();
      // switch (refAPIErrorDialogType.value.type) {
      //   case 1:
      //     getIngredientCandidate(searchText.value);
      //     break;
      //   case 2:
      //     registToShoppingList();
      //     break
      //   default:
      //     break;
      // }
    };

    return {
      initialKeyword,
      setSearchText,
      isCompleteSearch,
      onScroll,
      searchResult,
      isRaw,
      rawName,
      rawSelected,
      selectItem,
      selectRawItem,
      styleAppendButton,
      appendButtonAction,
      appendButtonText,
      isShowMessageDialog,
      messageDialogType,
      messageDialogTitle,
      messageDialogMessage,
      messageDialogNegativeLabel,
      messageDialogPositiveLabel,
      messageDialogNegativeAction,
      messageDialogPositiveAction,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      apiErrorDialogType,
      retryButtonTitle,
      okButtonTitle,
      cancelHandler,
      retryHandler
    };
  }
});
